import { useState } from 'react';

import { useIntl } from 'react-intl';
import { createResource, type RouteResource } from 'react-resource-router';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { assert, AssertionError, errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import messages from '../../../common/messages';
import type {
	AiNotificationPolicy,
	AiNotificationPolicyResponse,
	ChangeAiNotificationPolicyOrderParams,
	ChangeAiNotificationPolicyStateParams,
	CreateAiNotificationPolicyParams,
	DeleteAiNotificationPolicyParams,
	UpdateAiNotificationPolicyParams,
} from '../../../common/types';

import {
	changeGlobalGroupNotificationPolicyOrderEndpoint,
	createGlobalGroupNotificationPolicyEndpoint,
	deleteGlobalGroupNotificationPolicyEndpoint,
	disableGlobalGroupNotificationPolicyEndpoint,
	enableGlobalGroupNotificationPolicyEndpoint,
	getGlobalGroupNotificationPolicies,
	updateGlobalGroupNotificationPolicyEndpoint,
} from './endpoints';

export const globalGroupNotificationPolicyResource: RouteResource<
	AiNotificationPolicyResponse | undefined
> = createResource({
	type: 'globalGroupNotificationPolicyResource',
	// Global policies are not team-scoped, so the key must not vary by teamId or the same
	// payload is fetched and cached once per team.
	getKey: () => 'global-group-notification-policy',
	getData: async () => getGlobalGroupNotificationPolicies(),
	isBrowserOnly: true,
});

type GlobalNotificationPolicyService = {
	isChangingOrder: boolean;
	isDeleting: boolean;
	isDisabling: boolean;
	isEnabling: boolean;
	isCreating: boolean;
	isUpdating: boolean;
	deletePolicy: (payload: DeleteAiNotificationPolicyParams) => Promise<void>;
	disable: (payload: ChangeAiNotificationPolicyStateParams) => Promise<void>;
	enable: (payload: ChangeAiNotificationPolicyStateParams) => Promise<void>;
	create: (payload: CreateAiNotificationPolicyParams) => Promise<void>;
	updateOrder: (
		payload: ChangeAiNotificationPolicyOrderParams,
		updatedValue: AiNotificationPolicy[],
	) => Promise<void | Response>;
	update: (payload: UpdateAiNotificationPolicyParams) => Promise<void>;
};

export const useGlobalGroupNotificationPolicyService = (): GlobalNotificationPolicyService => {
	const { data, update } = useResource(globalGroupNotificationPolicyResource);
	const { formatMessage } = useIntl();
	const defaultCatch = useDefaultCatch();
	const flagApi = useFlags();
	const [isChangingOrder, setIsChangingOrder] = useState(false);
	const [isEnabling, setIsEnabling] = useState(false);
	const [isDisabling, setIsDisabling] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);

	assert(
		!!data,
		new AssertionError(
			'useGlobalGroupNotificationPolicyService',
			'We should not render useGlobalGroupNotificationPolicyService before data is fetched',
		),
	);

	const updateOrder = (
		payload: ChangeAiNotificationPolicyOrderParams,
		updatedValue: AiNotificationPolicy[],
	): Promise<void | Response> => {
		setIsChangingOrder(true);

		update(() => {
			return {
				...data,
				policies: updatedValue,
			};
		});

		return changeGlobalGroupNotificationPolicyOrderEndpoint(payload)
			.catch(
				defaultCatch({
					id: 'useGlobalGroupNotificationPolicyService-updateOrder',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.reorderError, {
								type: '',
							}),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsChangingOrder(false));
	};

	const enable = (payload: ChangeAiNotificationPolicyStateParams): Promise<void> => {
		setIsEnabling(true);

		return enableGlobalGroupNotificationPolicyEndpoint(payload)
			.then(() => {
				update(() => ({
					...data,
					policies: data.policies.map((policy) =>
						policy.id === payload.id ? { ...policy, enabled: true } : policy,
					),
				}));
			})
			.catch(
				defaultCatch({
					id: 'useGlobalGroupNotificationPolicyService-enable',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.enableError),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsEnabling(false));
	};

	const disable = (payload: ChangeAiNotificationPolicyStateParams): Promise<void> => {
		setIsDisabling(true);

		return disableGlobalGroupNotificationPolicyEndpoint(payload)
			.then(() => {
				update(() => ({
					...data,
					policies: data.policies.map((policy) =>
						policy.id === payload.id ? { ...policy, enabled: false } : policy,
					),
				}));
			})
			.catch(
				defaultCatch({
					id: 'useGlobalGroupNotificationPolicyService-disable',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.disableError),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsDisabling(false));
	};

	const deletePolicy = (payload: DeleteAiNotificationPolicyParams): Promise<void> => {
		setIsDeleting(true);

		return deleteGlobalGroupNotificationPolicyEndpoint(payload)
			.then(() => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.policyDelete, {
						name: payload.name,
						type: 'notification',
					}),
				});

				update(() => ({
					...data,
					policies: data.policies.filter((policy) => policy.id !== payload.id),
				}));
			})
			.catch(
				defaultCatch({
					id: 'useGlobalGroupNotificationPolicyService-deletePolicy',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.deleteError),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsDeleting(false));
	};

	const create = (payload: CreateAiNotificationPolicyParams): Promise<void> => {
		setIsCreating(true);

		return createGlobalGroupNotificationPolicyEndpoint(payload)
			.then((res) => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.policyAdded, {
						name: payload.name,
						type: 'notification',
					}),
				});

				const newData: AiNotificationPolicyResponse = {
					...data,
					policies: [...data.policies, res],
				};

				update(() => newData);
			})
			.catch(
				defaultCatch({
					id: 'useGlobalGroupNotificationPolicyService-create',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.addError, { type: 'notification' }),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsCreating(false));
	};

	const updatePolicy = (payload: UpdateAiNotificationPolicyParams): Promise<void> => {
		setIsUpdating(true);

		return updateGlobalGroupNotificationPolicyEndpoint(payload)
			.then((res) => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.policyEdit, {
						name: payload.name,
						type: 'notification',
					}),
				});

				const newData: AiNotificationPolicyResponse = {
					...data,
					policies: data.policies.map((policy) => {
						if (policy.id === payload.id) {
							return res;
						}

						return policy;
					}),
				};

				update(() => newData);
			})
			.catch(
				defaultCatch({
					id: 'useGlobalGroupNotificationPolicyService-updatePolicy',
					httpErrorHandler: (err) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.editError, { type: 'notification' }),
						});

						throw err;
					},
				}),
			)
			.finally(() => setIsUpdating(false));
	};

	return {
		isChangingOrder,
		isDeleting,
		isDisabling,
		isEnabling,
		isCreating,
		isUpdating,
		deletePolicy,
		disable,
		enable,
		create,
		updateOrder,
		update: updatePolicy,
	};
};
